import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { IconType } from 'react-icons';
import {
  TbBell,
  TbCalendarEvent,
  TbChevronDown,
  TbLayoutDashboard,
  TbMessageCircle,
  TbShieldLock,
  TbUsers,
} from 'react-icons/tb';
import { menuWrap, menuGroup, menuArrow, subMenu, subMenuWrap } from './style';
import { MENUS } from 'data/menu';
import useMenus from './useMenus';
import { useAppSelector } from 'stores';
import { canSeeMenu } from './menuAccess';

const MENU_ICONS: Record<number, IconType> = {
  0: TbUsers,
  1: TbCalendarEvent,
  2: TbLayoutDashboard,
  3: TbBell,
  4: TbMessageCircle,
  5: TbShieldLock,
};

interface Props {
  isCollapsed?: boolean;
  onExpand?: () => void;
}

function Menus({ isCollapsed = false, onExpand }: Props) {
  const { first, current, pathname, handleFirst } = useMenus();
  const [isOpenCurrent, setIsOpenCurrent] = useState(true);
  const user = useAppSelector((s) => s.auth.user);

  const visibleMenus = useMemo(
    () =>
      MENUS.map((m) => ({
        ...m,
        items: m.items.filter((item) => canSeeMenu(item.to, user)),
      })).filter((m) => m.items.length > 0),
    [user],
  );

  const isOpenSubMenu = (key: number) => {
    if (key === current) return isOpenCurrent;
    return first === key;
  };

  const handleClickMenu = (key: number) => {
    if (isCollapsed) {
      onExpand?.();
      if (key !== current && key !== first) handleFirst(key);
      return;
    }
    if (key === current) setIsOpenCurrent((prev) => !prev);
    else handleFirst(key);
  };

  useEffect(() => {
    setIsOpenCurrent(true);
  }, [current]);

  return (
    <ul css={menuWrap}>
      {visibleMenus.map((m, i) => {
        const Icon = MENU_ICONS[m.key] ?? TbLayoutDashboard;
        const isOpen = !isCollapsed && isOpenSubMenu(m.key);

        return (
          <React.Fragment key={i}>
            <li
              css={menuGroup(m.key === current, isCollapsed)}
              onClick={() => handleClickMenu(m.key)}
              title={isCollapsed ? m.title : undefined}
            >
              <div>
                <Icon size={20} />
                <span>{m.title}</span>
              </div>
              <TbChevronDown size={16} css={menuArrow(isOpen)} />
            </li>
            <div css={subMenuWrap(isOpen)}>
              {(() => {
                const activeItem = m.items.reduce<string | null>(
                  (best, item) =>
                    pathname.startsWith(item.to) &&
                    (!best || item.to.length > best.length)
                      ? item.to
                      : best,
                  null,
                );
                return m.items.map((d, i) => (
                  <NavLink
                    key={i}
                    to={d.to}
                    css={subMenu(current === m.key && d.to === activeItem)}
                  >
                    {d.title}
                  </NavLink>
                ));
              })()}
            </div>
          </React.Fragment>
        );
      })}
    </ul>
  );
}

export default Menus;
